/** Localities API — list + per-locality detail. */
import { http } from "@/lib/api";
import { thresholds } from "@/lib/thresholds";

export interface LocalityListRow {
  name: string;
  latitude: number | null;
  longitude: number | null;
  house_count: number;
  sensor_count: number;
  daily_consumption_m3: number;
  avg_house_m3: number;
  avg_sensor_m3: number;
  /** This locality's own water balance (`core.nrw_basis.zone_window_totals`).
   * `null` when the zone has no SIV for the window (no DMA inlet), not 0. */
  nrw_percent: number | null;
  /** IWA A–F grade from `nrw_percent` — colour via `statusRegistry.nrwGrade`. */
  nrw_grade: string | null;
  /** Active detected-anomaly episode count for this locality. `null` while the
   * nightly CUSUM precompute cache is cold, not 0. */
  /** Open alerts here (ongoing, not closed by an operator) — the home KPI's definition. */
  open_alerts: number | null;
  /** Inlet days the NRW figure covers, and the last of them. */
  nrw_days: number | null;
  nrw_as_of: string | null;
}

export interface LocalityStats {
  name: string;
  latitude: number | null;
  longitude: number | null;
  house_count: number;
  sensor_count: number;
  residential: number;
  commercial: number;
  total_daily_m3: number;
  avg_house_m3: number;
  as_of: string;
  nrw_percent: number | null;
  nrw_grade: string | null;
  /** Open alerts here (ongoing, not closed by an operator) — the home KPI's definition. */
  open_alerts: number | null;
  /** Inlet days the NRW figure covers, and the last of them. */
  nrw_days: number | null;
  nrw_as_of: string | null;
}

export interface LocalityTrendPoint {
  day: string;
  consumption_m3: number;
}

export interface HouseRow {
  house_id: string;
  sensor_id: string | null;
  user_type: string | null;
  area_marla: number | null;
  latitude: number | null;
  longitude: number | null;
  consumption_m3: number;
  sensor_status: string | null;
}

export interface TopConsumerRow {
  house_id: string;
  sensor_id: string | null;
  user_type: string | null;
  area_marla: number | null;
  latitude: number | null;
  longitude: number | null;
  weekly_consumption_m3: number;
  monthly_consumption_m3: number;
  lifetime_consumption_m3: number;
  /** Four-tier meter reporting status (`statusRegistry.sensor`), same as the
   * sensor map/registry. `null` when the house has no sensor. */
  sensor_status: string | null;
}

async function get<T>(url: string, params?: object, signal?: AbortSignal): Promise<T> {
  const { data } = await http.get<T>(url, { params, signal });
  return data;
}

const enc = encodeURIComponent;

export const fetchLocalities = (s?: AbortSignal) =>
  get<LocalityListRow[]>("/localities/list", undefined, s);
export const fetchLocalityStats = (name: string, s?: AbortSignal) =>
  get<LocalityStats>(`/localities/${enc(name)}/stats`, undefined, s);
export const fetchLocalityTrends = (
  name: string,
  days = thresholds().default_analysis_days,
  s?: AbortSignal,
) => get<LocalityTrendPoint[]>(`/localities/${enc(name)}/trends`, { days }, s);
export const fetchLocalityHouses = (
  name: string,
  days = thresholds().default_analysis_days,
  s?: AbortSignal,
) => get<HouseRow[]>(`/localities/${enc(name)}/houses`, { days }, s);
export const fetchLocalityTopConsumers = (name: string, limit = 10, s?: AbortSignal) =>
  get<TopConsumerRow[]>(`/localities/${enc(name)}/top-consumers`, { limit }, s);
